import Badge from "@/components/Badge";
import Pagination from "@/components/Pagination";
import Table from "@/components/Table";
import BodyImageWithInfoColumn from "@/components/Table/table-body/BodyImageWithInfoColumn";
import HeaderColumn from "@/components/Table/table-header/HeaderColumn";
import { ReactComponent as EYE_ICON } from "@/assets/icons/eye.svg";
import { useNavigate } from "react-router-dom";
import HeaderColumnTooltip from "@/components/Table/table-header/HeaderColumnTooltip";
import getStockInteractor from "@/@core/interactor/stock/get-stocks.interactor";
import { ChangeEvent, FC, useEffect, useState } from "react";
import { Stock } from "@/@core/entity/stock.entity";
import { stringToDate } from "@/utils/date";
import Input from "@/components/Input";
import Button from "@/components/Button";

interface TableSectionPropTypes {
  isRefetchStock: boolean;
}

let debounceSearch: any;

const TableSection: FC<TableSectionPropTypes> = ({ isRefetchStock }) => {
  const navigate = useNavigate();
  const [stockQuatities, setStockQuatities] = useState<Stock[]>([]);
  const [activePage, setActivePage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [keyword, setKeyword] = useState<string>("");

  useEffect(() => {
    if (isRefetchStock) {
      getStockQuantity(1);
    }
  }, [isRefetchStock]);

  useEffect(() => {
    getStockQuantity(activePage);
  }, []);

  const getStockQuantity = async (page: number, serial_number = "") => {
    try {
      const response = await getStockInteractor({
        limit: 10,
        page,
        serial_number,
        type: 1,
      });

      const {
        data: { data, pagination },
      } = response;

      setStockQuatities(data);
      if (pagination) {
        setActivePage(pagination.current_page);
        setTotalPage(pagination.last_page);
      }
    } catch (error: any) {
      setStockQuatities([]);
      setActivePage(1);
      setTotalPage(1);
    }
  };

  const onChangeKeyword = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setKeyword(val);

    clearTimeout(debounceSearch);
    debounceSearch = setTimeout(() => {
      getStockQuantity(1, val);
    }, 2000);
  };

  const onKeywordEnter = (e: any) => {
    const keyCode = e.keyCode;
    const val = e.target.value;

    if (keyCode === 13) {
      clearTimeout(debounceSearch);
      getStockQuantity(1, val);
    }
  };

  const resetFilter = () => {
    getStockQuantity(1, "");
    setKeyword("");
  };
  const onChangePagination = (e: any) => {
    setActivePage(e);
    getStockQuantity(e);
  };

  return (
    <>
      <div className='table-control-section'>
        <div>
          <label>Search for UID</label>
          <Input
            style={{ width: "360px" }}
            value={keyword}
            withIcon
            type='search'
            placeholder='UID,UID,UID'
            onChange={onChangeKeyword}
            onKeyDown={onKeywordEnter}
          />
        </div>
        <div className="btn-wrapper">
          <Button
            onClick={resetFilter}
            label="Reset Filter"
            type="secondary"
            size="md"
            disabled={!keyword}
          />
        </div>
      </div>
      <div className="table-section">
        <Table>
          <thead>
            <tr>
              <HeaderColumn label="Stock Qty ID" alignLabel="text-left" />
              <HeaderColumn label="Product" alignLabel="text-left" />
              <HeaderColumnTooltip
                toolTipText="Stock"
                label="Stock"
                alignLabel="text-left"
              />
              <HeaderColumn label="Warehouse" alignLabel="text-center" />
              <HeaderColumn label="Status" alignLabel="text-center" />
              <HeaderColumn label="Created by" alignLabel="text-center" />
              <HeaderColumn label="Created at" alignLabel="text-left" />
              <HeaderColumn label="" alignLabel="text-left" />
            </tr>
          </thead>
          <tbody>
            {stockQuatities.map((stock) => (
              <tr key={stock.id}>
                <td width="12%">
                  <div className="text-title">
                    <span>{stock.id}</span>
                  </div>
                </td>
                <BodyImageWithInfoColumn
                  width="300px"
                  imgSrc={`${process.env.REACT_APP_API_URL}/file/${stock.product_photo}`}
                  roundedImg="0px"
                  textName={stock.product_name}
                  textDesc={stock.product_sku}
                />
                <td width="5%">
                  <div className="text item-center">
                    <span>{stock.stock}</span>
                  </div>
                </td>
                <td width="15%">
                  <div className="text item-center">
                    <span>{stock.warehouse}</span>
                  </div>
                </td>
                <td width="100px">
                  <div className="text item-center">
                    <Badge
                      label={stock.status === 2 ? "Success" : "Failed"}
                      labelColor={stock.status === 2 ? "#067647" : "#B42318"}
                      backgroundColor={
                        stock.status === 2 ? "#ECFDF3" : "#FEF3F2"
                      }
                    />
                  </div>
                </td>
                <td width="15%">
                  <div className="text item-center">
                    <span>{stock.created_by}</span>
                  </div>
                </td>
                <td width="20%">
                  <div className="text">
                    <span>
                      {stringToDate(
                        stock?.created_at,
                        "dd month yyyy hours:minutes"
                      )}
                    </span>
                  </div>
                </td>
                <td width="20%">
                  <div className="cta-wrapper">
                    <button
                      onClick={() =>
                        navigate("/inventory/stock-quantity/add-stock", {
                          state: { stockId: stock.id },
                        })
                      }
                    >
                      <EYE_ICON />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Pagination
          activePage={activePage}
          totalPage={totalPage}
          onChange={onChangePagination}
        />
      </div>
    </>
  );
};

export default TableSection;
